import React, { ReactNode } from "react";
import { Building2Icon, DoorOpenIcon, MapPinIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";
import {
  companyMatchTone,
  useCustomerCompanyMatches,
} from "@/hooks/useCustomerCompanyMatches";
import { dates } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { CompensationType, WorkExperienceType } from "@/types";

// TEMPORARY: fuzzy keyword highlighting for the search-v2 candidate sheet.
const normalize = (word: string) =>
  word.toLowerCase().replace(/[^a-z0-9]/g, "");

const editDistanceWithin = (a: string, b: string, max: number): boolean => {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr.push(
        Math.min(
          prev[j] + 1,
          curr[j - 1] + 1,
          prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
        ),
      );
    }
    prev = curr;
  }
  return prev[b.length] <= max;
};

// Fuzzy tolerance: exact under 4 chars, 1 edit under 8, else 2 edits.
const isFuzzyMatch = (a: string, b: string) =>
  editDistanceWithin(a, b, a.length < 4 ? 0 : a.length < 8 ? 1 : 2);

// Wraps fuzzy matches of 1-3 word keyword phrases in the Search V2 treatment.
const highlightMatches = (text: string, keywords?: string) => {
  const keywordWords = keywords?.split(/\s+/).map(normalize).filter(Boolean);
  if (!keywordWords?.length) return text;

  const textWords = text.split(/(\s+)/); // keep whitespace tokens for reassembly
  const nodes: (string | React.JSX.Element)[] = [];
  for (let i = 0; i < textWords.length; i++) {
    let matchedTokenCount = 0;
    // Try 3-word phrases first so longer matches win. Word tokens are every
    // other entry (whitespace interleaved), so a phrase of n words spans
    // 2n - 1 tokens.
    for (
      let phraseSize = 3;
      phraseSize >= 1 && !matchedTokenCount;
      phraseSize--
    ) {
      const tokenCount = phraseSize * 2 - 1;
      const window = textWords
        .slice(i, i + tokenCount)
        .map(normalize)
        .filter(Boolean);
      if (window.length !== phraseSize) continue;
      for (let k = 0; k + phraseSize <= keywordWords.length; k++) {
        const phrase = keywordWords.slice(k, k + phraseSize);
        if (isFuzzyMatch(window.join(" "), phrase.join(" "))) {
          matchedTokenCount = tokenCount;
          break;
        }
      }
    }
    if (matchedTokenCount) {
      nodes.push(
        <mark
          className="mx-0.5 inline rounded-md border border-blue-100 bg-blue-50 px-1.5 py-0.5 text-[0.95em] font-normal text-blue-700"
          key={i}
        >
          {textWords.slice(i, i + matchedTokenCount).join("")}
        </mark>,
      );
      i += matchedTokenCount - 1;
    } else {
      nodes.push(textWords[i]);
    }
  }
  return nodes;
};

const MAX_HIGHLIGHTS_PER_EXPERIENCE = 10;
const MAX_HIGHLIGHTS_PER_DESCRIPTION = 4;

const renderRelevantExperienceHighlights = (
  description: string,
  _remainingHighlightCount: number,
  _isTypeOnly = false,
) => {
  return { content: description, highlightCount: 0 };
};

export const WorkExperienceHighlightedText = ({
  description,
  isTypeOnly = false,
}: {
  description: string;
  isTypeOnly?: boolean;
}) => {
  return renderRelevantExperienceHighlights(
    description,
    MAX_HIGHLIGHTS_PER_DESCRIPTION,
    isTypeOnly,
  ).content;
};

export const WorkExperience = ({
  companyContent,
  dateActions,
  datesContent,
  descriptionsContent,
  headerActions,
  highlightKeywords,
  highlightRelevantTerms = false,
  metaContent,
  titleContent,
  workExperience,
}: {
  companyContent?: ReactNode;
  dateActions?: ReactNode;
  datesContent?: ReactNode;
  descriptionsContent?: ReactNode;
  headerActions?: ReactNode;
  highlightKeywords?: string;
  highlightRelevantTerms?: boolean;
  metaContent?: ReactNode;
  titleContent?: ReactNode;
  workExperience: WorkExperienceType;
}) => {
  /* React Hooks */
  const { matchCompany } = useCustomerCompanyMatches();

  const dateDisplay =
    dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience({
      startMonth: workExperience.startMonth ?? undefined,
      startYear: workExperience.startYear ?? undefined,
      endMonth: workExperience.endMonth ?? undefined,
      endYear: workExperience.endYear ?? undefined,
      current: workExperience.currentPosition ?? undefined,
    });

  const tenure =
    dates.calculateTenureForCertificationOrEducationOrWorkExperience({
      startMonth: workExperience.startMonth,
      startYear: workExperience.startYear,
      endMonth: workExperience.endMonth,
      endYear: workExperience.endYear,
      current: workExperience.currentPosition,
    });

  const payUnit =
    workExperience.compensationType === CompensationType.HOURLY ? "hr" : "yr";

  // Drizzle returns numeric DB columns as strings for precision, so we parse to Number for comma formatting
  const finalPayDisplay = workExperience.finalPay
    ? `$${Number(workExperience.finalPay).toLocaleString()}/${payUnit}`
    : null;
  const startPayDisplay = workExperience.startPay
    ? `$${Number(workExperience.startPay).toLocaleString()}/${payUnit}`
    : null;

  const cleanedDescriptions =
    workExperience.descriptions?.filter((d) => d.trim()) ?? [];

  const reasonForLeaving = workExperience.reasonForLeaving?.trim();

  // Resume parsing can produce work experiences with missing titles or companies.
  // Show placeholders so staffers can spot and fix incomplete entries.
  const title = workExperience.title?.trim();
  const companyName =
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    workExperience.internalCompany?.name.trim() ||
    workExperience.company?.trim();
  const dot = <span className="text-slate-500">·</span>;
  const hasLocation = !!workExperience.location;
  const hasPay = !!finalPayDisplay;
  let relevantHighlightCount = 0;

  const renderCompanyName = () => {
    if (!companyName) {
      return (
        <Text className="text-sm font-normal italic text-red-400">
          [Missing Company]
        </Text>
      );
    }
    // Customer match: color the employer name so the risk reads inline.
    const match = matchCompany(companyName, workExperience.currentPosition);
    if (match) {
      const tone = companyMatchTone(match.reason);
      return (
        <Text
          className={cn(
            "rounded-md border px-1.5 py-0.5 text-sm font-medium",
            tone.container,
            tone.content,
          )}
        >
          {companyName}
        </Text>
      );
    }
    return (
      <Text className="text-sm font-normal text-slate-600">{companyName}</Text>
    );
  };

  const renderTitle = () => {
    if (titleContent) return titleContent;

    if (title) {
      return (
        <Text className="text-sm font-medium capitalize text-slate-900">
          {title}
        </Text>
      );
    }

    return (
      <Text className="text-sm font-semibold uppercase italic tracking-wide text-red-400">
        [Missing Title]
      </Text>
    );
  };

  const renderHeaderAside = () => {
    if (headerActions) {
      return <Row className="shrink-0 items-center">{headerActions}</Row>;
    }

    if (datesContent) return null;

    return (
      <Column className="shrink-0 items-end justify-between gap-1.5 text-right">
        <Row className="items-center gap-1">
          {dateActions}
          {dateDisplay && (
            <span className="text-sm text-slate-500">{dateDisplay}</span>
          )}
        </Row>
        {tenure && <span className="text-sm text-slate-500">{tenure}</span>}
      </Column>
    );
  };

  return (
    <Row className="w-full items-start gap-4">
      <Row className="h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 text-slate-400">
        {workExperience.internalCompany?.logoUrl ? (
          <img
            alt={workExperience.internalCompany.name}
            className="h-full w-full object-cover"
            src={workExperience.internalCompany.logoUrl}
          />
        ) : (
          <Building2Icon className="h-5 w-5" />
        )}
      </Row>
      <Column
        className={cn("min-w-0 flex-1", datesContent ? "gap-3" : "gap-0.5")}
      >
        <Row className="min-h-11 flex-wrap items-stretch justify-between gap-3">
          <Column
            className={cn(
              "justify-start gap-1",
              // Editors keep their min-content width so the dates block wraps
              // below instead of overflowing into them.
              datesContent ? "min-w-fit flex-1 gap-3" : "min-w-0",
            )}
          >
            <Row className={cn(datesContent && "w-full")}>{renderTitle()}</Row>
            <Row>{companyContent ?? renderCompanyName()}</Row>
          </Column>
          {datesContent}
          {renderHeaderAside()}
        </Row>
        {headerActions && !datesContent && dateDisplay && (
          <Row className="flex-wrap items-center gap-x-2 text-sm text-slate-500">
            <span>{dateDisplay}</span>
            {tenure && (
              <>
                {dot}
                <span>{tenure}</span>
              </>
            )}
          </Row>
        )}
        {metaContent ??
          ((hasLocation || hasPay) && (
            <Row className="flex-wrap items-center gap-x-2 text-sm text-slate-500">
              {hasLocation && (
                <Row className="items-center gap-1 text-xs">
                  <MapPinIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  <span>{workExperience.location}</span>
                </Row>
              )}
              {hasLocation && hasPay && dot}
              {hasPay && (
                <Row className="items-baseline gap-1.5">
                  {startPayDisplay && (
                    <span className="text-xs line-through">
                      {startPayDisplay}
                    </span>
                  )}
                  <span>{finalPayDisplay}</span>
                </Row>
              )}
            </Row>
          ))}
        {descriptionsContent ??
          (cleanedDescriptions.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {cleanedDescriptions.map((description, i) => {
                const highlightedDescription =
                  renderRelevantExperienceHighlights(
                    description,
                    MAX_HIGHLIGHTS_PER_EXPERIENCE - relevantHighlightCount,
                    highlightRelevantTerms,
                  );
                relevantHighlightCount += highlightedDescription.highlightCount;

                return (
                  <li
                    className="mt-1 text-sm font-light leading-relaxed text-[#121212] dark:text-[#FAFAFA]"
                    key={i}
                  >
                    {highlightRelevantTerms
                      ? highlightedDescription.content
                      : highlightMatches(description, highlightKeywords)}
                  </li>
                );
              })}
            </ul>
          ))}
        {reasonForLeaving ? (
          <Row className="mt-1 items-start gap-1">
            <DoorOpenIcon className="mt-0.5 h-3 w-3 shrink-0 text-slate-300" />
            <Text className="text-xs font-normal text-slate-400">
              Reason for leaving: {reasonForLeaving}
            </Text>
          </Row>
        ) : null}
      </Column>
    </Row>
  );
};
